import React, { ChangeEvent, ClipboardEvent, KeyboardEvent, useCallback } from 'react';
import PaymentTabs from './payment-tabs/payment-tabs';
import {
    newCreditCardActions,
    rootActions,
    savedCreditCardActions,
    useAppDispatch,
    useAppSelector
} from '../../data/store';
import { CreditCardTabTabsEnum } from '../../enums/credit-card-tab';
import { PaymentTabsEnum } from '../../enums/payment-tabs-enum';
import { checkIsSubscription } from '../../domain/shopping-cart-provider';
import { getIsChina } from '../../../../../domain/hidden-data-provider';
import GrayBox from '../shared/gray-box';
import CVVInfoPopup from '../../../cart-shared-bridger-pay/ui/cvv-info-popup';
import SavedCreditCardsShared from '../../../cart-shared-bridger-pay/ui/saved-credit-cards';
import {
    removeInvisibleCharsOnPaste,
    updateControl,
    validateControl
} from '../../../../../domain/validation/validation';
import { ICreditCard } from '../../interfaces';
import NewCreditCardShared from '../../../cart-shared-bridger-pay/ui/new-credit-card';
import { getSelectedTab } from './payment-tabs/payment-tab';

/**
 * The payment part of cart step #3.
 */
const Payment = () => {

    const dispatch = useAppDispatch();

    const selectedPaymentTab = useAppSelector(store => store.root.selectedPaymentTab);
    const selectedCreditCardTab = useAppSelector(store => store.root.selectedCreditCardTab);

    const cartData = useAppSelector(store => store.root.cartData);
    const savedCreditCards = useAppSelector(store => store.savedCreditCard.savedCreditCards);
    const savedCreditCardToken = useAppSelector(store => store.savedCreditCard.savedCreditCardToken);
    const cvvControl = useAppSelector(store => store.savedCreditCard.cvvControl);
    const nameOnCardControl = useAppSelector(store => store.newCreditCard.nameOnCardControl);
    const rememberMe = useAppSelector(store => store.newCreditCard.rememberMe);
    const sbsDebitAllowed = useAppSelector(store => store.root.sbsDebitAllowed);

    const isChina = getIsChina();
    const isSubscription = checkIsSubscription(cartData);

    const isZeroPurchase = cartData.priceData.totalPrice < 1 && !checkIsSubscription(cartData);
    const cvvInfoPopupShown = useAppSelector(store => store.newCreditCard.cvvInfoPopupShown);
    const isSbsFreeTrial = checkIsSubscription(cartData) && cartData.priceData.totalPrice <= 1;

    const selectedTab = getSelectedTab(cartData, selectedPaymentTab);

    const cvvChanged = (evt: ChangeEvent<HTMLInputElement>) => {
        const $target = evt.target as HTMLInputElement;

        dispatch(
            savedCreditCardActions.setControl({
                controlName: 'cvvControl',
                control: updateControl(cvvControl, $target.value, true)
            })
        );
    };

    const updateSavedCards = useCallback((_savedCards: ICreditCard[]) => {

        dispatch(
            savedCreditCardActions.main({
                savedCreditCards: _savedCards,
            })
        );

        if(_savedCards.length <= 0){

            // jump to 'new credit card' tab
            dispatch(
                rootActions.main({
                    selectedCreditCardTab: CreditCardTabTabsEnum.NewCreditCard,
                })
            );
            return;
        }

        dispatch(
            savedCreditCardActions.main({
                savedCreditCardToken: _savedCards[0].token,
                savedCreditCardEncryptedCvv: _savedCards[0].encrypted_cvv,
                savedCreditCardKenticoItemID: _savedCards[0].kenticoItemID,
                savedCreditCardChecksum: _savedCards[0].card_checksum,
            })
        );
    }, [ dispatch ]);

    const selectSavedCard = (savedCard: ICreditCard) => {

        if(savedCreditCardToken !== savedCard.token){
            dispatch(
                savedCreditCardActions.setControl({
                    controlName: 'cvvControl',
                    control: updateControl(cvvControl, '', false)
                })
            );
        }

        dispatch(
            savedCreditCardActions.main({
                savedCreditCardToken: savedCard.token,
                savedCreditCardEncryptedCvv: savedCard.encrypted_cvv,
                savedCreditCardKenticoItemID: savedCard.kenticoItemID,
                savedCreditCardChecksum: savedCard.card_checksum,
            })
        );
    };

    const handleAddNewCardButton = () => {

        dispatch(
            rootActions.main({
                selectedCreditCardTab: CreditCardTabTabsEnum.NewCreditCard,
            })
        );
    };

    const showCvvInfoPopup = () => {
        dispatch(
            newCreditCardActions.main({
                cvvInfoPopupShown: true,
            })
        );
    };

    const newCreditCardBackBtnClicked = () => {
        dispatch(
            rootActions.main({
                selectedCreditCardTab: CreditCardTabTabsEnum.SavedCreditCards,
            })
        );
    };

    const setBloxController = (_bloxController: any) => {
        dispatch(
            newCreditCardActions.main({
                bloxController: _bloxController,
            })
        );
    };

    const rememberMeOnChangeEvent = (evt: ChangeEvent<HTMLInputElement>) => {
        dispatch(
            newCreditCardActions.main({
                rememberMe: evt.target.checked,
            })
        )
    };

    const nameOnCardChangeEvent = (evt: ChangeEvent) => {
        const $target = evt.target as HTMLInputElement;

        dispatch(
            newCreditCardActions.setControl({
                controlName: 'nameOnCardControl',
                control: updateControl(nameOnCardControl, $target.value, !nameOnCardControl.isValid),
            })
        );
    };

    const nameOnCardBlurEvent = () => {
        dispatch(
            newCreditCardActions.setControl({
                controlName: 'nameOnCardControl',
                control: validateControl(nameOnCardControl),
            })
        );
    };

    const nameOnCardKeyupEvent = (evt: KeyboardEvent) => {

        if(evt.key === 'Escape') {
            dispatch(
                newCreditCardActions.setControl({
                    controlName: 'nameOnCardControl',
                    control: updateControl(nameOnCardControl, '', !nameOnCardControl.isValid)
                })
            );
        }
    };

    const nameOnCardPasteEvent = (evt: ClipboardEvent<HTMLInputElement>) => {

        dispatch(
            newCreditCardActions.setControl({
                controlName: 'nameOnCardControl',
                control: updateControl(nameOnCardControl,  removeInvisibleCharsOnPaste(evt), !nameOnCardControl.isValid)
            })
        );
    };

    return (
        <div className="mb-10">

            {
                isZeroPurchase && (
                    <div className="info-box mb-4"><p>Your order total is $0, therefore no payment information is required.</p></div>
                )
            }

            {
                !isZeroPurchase &&
                <>
                    <PaymentTabs />

                    {
                        (selectedTab === PaymentTabsEnum.CreditCard) && isSubscription && !sbsDebitAllowed &&
                        <GrayBox classes="mb-4">
                            All major credit cards accepted. Subscriptions cannot be purchased with debit/prepaid cards.
                        </GrayBox>
                    }

                    {
                        (selectedTab === PaymentTabsEnum.CreditCard) &&
                        selectedCreditCardTab === CreditCardTabTabsEnum.NewCreditCard &&
                        <NewCreditCardShared
                            isSubscription={ isSubscription }
                            setBloxController={ setBloxController }

                            rememberMe={ rememberMe }
                            rememberMeOnChangeEvent={ rememberMeOnChangeEvent }

                            nameOnCardChangeEvent={ nameOnCardChangeEvent }
                            nameOnCardPasteEvent={ nameOnCardPasteEvent }
                            nameOnCardBlurEvent={ nameOnCardBlurEvent }
                            nameOnCardKeyupEvent={ nameOnCardKeyupEvent }
                            nameOnCardControl={ nameOnCardControl }

                            showCvvInfoPopup={ showCvvInfoPopup }

                            showBackButton={ savedCreditCards && savedCreditCards.length > 0 }
                            back={ newCreditCardBackBtnClicked }
                        />
                    }

                    {
                        (selectedTab === PaymentTabsEnum.CreditCard) && selectedCreditCardTab === CreditCardTabTabsEnum.SavedCreditCards &&
                        <SavedCreditCardsShared
                            isSubscription={ isSubscription }

                            savedCreditCards={ savedCreditCards }
                            savedCreditCardToken={ savedCreditCardToken }

                            cvvControl={ cvvControl }
                            cvvChanged={ cvvChanged }

                            updateSavedCards={ updateSavedCards }
                            selectSavedCard={ selectSavedCard }

                            handleAddNewCardButton={ handleAddNewCardButton }
                            showCvvInfoPopup={ showCvvInfoPopup }
                        />
                    }

                    {
                        (!isChina) && (selectedTab === PaymentTabsEnum.PayPal) && (
                            <>
                                {
                                    isSbsFreeTrial &&
                                    <div className="bg-gray-100 border rounded text-gray-700 py-4 px-6">
                                        Once you click Subscribe you will be redirected to PayPal.
                                        A temporary $1 USD charge will appear to verify your PayPal account. We do not collect the charge; it will be canceled instantly once your account is verified. Once you have completed the process on PayPal you will be redirected back to waves.com.
                                    </div>
                                }

                                {
                                    !isSbsFreeTrial &&
                                    <div className="bg-gray-100 border rounded text-gray-700 py-4 px-6">
                                        Once you click {isSubscription ? 'Subscribe' : 'Confirm Order'} you
                                        will be
                                        redirected to PayPal. Once you have completed payment on PayPal you will be
                                        redirected
                                        back to waves.com.
                                    </div>
                                }
                            </>
                        )
                    }

                    {
                        (!isChina) && (selectedTab === PaymentTabsEnum.ApplePay) && (
                            <div className="bg-gray-100 border rounded text-gray-700 py-4 px-6">
                                {
                                    !isSbsFreeTrial &&
                                    <>
                                        Once you click {isSubscription ? 'Subscribe' : 'Confirm Order'} you will need to complete the payment on your phone.
                                    </>
                                }

                                {
                                    isSbsFreeTrial &&
                                    <>
                                        Once you click Subscribe you will need to complete the process on your phone. A temporary $1 USD charge will appear to verify your Apple Pay account. We do not collect the charge; it will be canceled instantly.
                                    </>
                                }

                            </div>
                        )
                    }

                    {/* {
                        isAliPaySelected && (
                            <div className="bg-gray-100 border rounded text-gray-700 py-4 px-6">
                                Once you click { isSubscription ? 'Subscribe' : 'Confirm Order' } you will be redirected to AliPay. Once you have completed payment on AliPay you will be redirected back to waves.com.
                            </div>
                        )
                    }*/}
                </>
            }

            {
                cvvInfoPopupShown &&
                <CVVInfoPopup onClose={ () => {
                    dispatch(
                        newCreditCardActions.main({
                            cvvInfoPopupShown: false,
                        })
                    );
                }} />
            }
        </div>
    )
};

export default Payment;